<template>
  <b-container fluid>
    <admin-preloader :show="loading"/>
    <admin-breadcrumb :items="[{ text: 'Taxonomies', path: {name: 'admin-taxonomies'} }]" name="Categories" no-title/>
    <b-row>
      <b-col cols="12">
        <admin-panel title="Categories">
          <template v-slot:header-right>
            <b-form-select
                class="w-auto mr-2"
                v-model="category_type_filter"
                :options="categoryTypeOptions"
            />
            <no-permission-wrapper :show="!canCreateTaxonomies">
              <b-btn variant="success" @click="openNewModal" :disabled="!canCreateTaxonomies">Add new Category</b-btn>
            </no-permission-wrapper>
          </template>
          <b-table
              :fields="table_fields"
              :items="categories"
              table-class="text-nowrap"
              responsive
          >
            <template #cell(name)="data">
              <h4 class="mb-0">{{ data.item.name }}</h4>
            </template>
            <template #cell(categoryType)="data">
              <span>{{ getCategoryTypeName(data.item.category_type_id) }}</span>
            </template>
            <template #cell(actions)="data">
              <no-permission-wrapper :show="!canUpdateTaxonomies">
                <b-btn variant="primary" @click.prevent="openEditModal(data.item)" :disabled="!canUpdateTaxonomies">Edit</b-btn>
              </no-permission-wrapper>
              <no-permission-wrapper :show="!canDeleteTaxonomies">
                <b-btn variant="danger" @click.prevent="confirmDelete(data.item)" :disabled="!canDeleteTaxonomies">Delete</b-btn>
              </no-permission-wrapper>
            </template>
          </b-table>
        </admin-panel>
      </b-col>
    </b-row>
    <admin-category-edit-modal
        v-if="edit_category"
        :category="edit_category"
        @cancel="edit_category = null"
        @success="onCategoryEdited"
        visible
    />
    <admin-category-add-modal
        ref="newModal"
        @success="onCategoryAdded"
    />
  </b-container>
</template>

<script>
import AdminPanel from "../components/AdminPanel";
import {API_ROUTES} from "../api";
import AdminCategoryEditModal from "../components/AdminCategoryEditModal";
import AdminCategoryAddModal from "../components/AdminCategoryAddModal";
import AdminPreloader from "../components/AdminPreloader";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import NoPermissionWrapper from "../components/NoPermissionWrapper.vue";

export default {
  name: "AdminCategories",
  components: {
    NoPermissionWrapper,
    AdminBreadcrumb, AdminPreloader, AdminCategoryAddModal, AdminCategoryEditModal, AdminPanel},
  data() {
    return {
      loading: false,
      table_fields: [
        {
          key: "name",
          thClass: "border-top-0",
          tdClass: "align-middle"
        },
        {
          key: "categoryType",
          label: "Category Type",
          thClass: "border-top-0",
          tdClass: "align-middle"
        },
        {
          key: "actions",
          thClass: "border-top-0",
          tdClass: "align-middle"
        }
      ],
      edit_category: null,
      category_type_filter: ""
    }
  },
  computed: {
    canCreateTaxonomies() {
      return this.$store.getters['auth/hasAdminPermissions']('taxonomy_create');
    },
    canUpdateTaxonomies() {
      return this.$store.getters['auth/hasAdminPermissions']('taxonomy_update');
    },
    canDeleteTaxonomies() {
      return this.$store.getters['auth/hasAdminPermissions']('taxonomy_delete');
    },
    search() {
      return this.$store.state.current.search;
    },
    categories() {
      let categories = this.$store.state.data.categories;
      if (this.search) {
        let search = this.search.toLowerCase();
        categories = categories.filter(c => c.name.toLowerCase().indexOf(search) >= 0);
      }
      if (this.category_type_filter) {
        categories = categories.filter(c => c.category_type_id === this.category_type_filter);
      }

      return categories.sort((item1, item2) => {
        let ct1 = this.getCategoryTypeName(item1.category_type_id).toLowerCase(),
            ct2 = this.getCategoryTypeName(item2.category_type_id).toLowerCase();
        if (ct1 < ct2) {
          return -1;
        }
        if (ct1 > ct2) {
          return 1;
        }
        let name1 = item1.name.toLowerCase(),
            name2 = item2.name.toLowerCase();
        if (name1 < name2) {
          return -1;
        }
        if (name1 > name2) {
          return 1;
        }
        return 0;
      });
    },
    categoryTypeOptions() {
      let categoryTypes = this.$store.state.data.category_types.map(ct => ({
        value: ct.id,
        text: ct.name
      }));
      categoryTypes.unshift({
        value: '',
        text: 'All Category Types'
      });
      return categoryTypes;
    }
  },
  methods: {
    checkPermission(permission) {
      if (!this[permission]) {
        this.$bvToast.toast('Sorry! You don\'t have permission to do this action', {
          title: 'Denied!',
          variant: 'danger'
        });
        return false;
      }
      return true;
    },
    openNewModal() {
      if (!this.checkPermission('canCreateTaxonomies')) {
        return;
      }
      this.$refs.newModal.open();
    },
    getCategoryTypeName(categoryTypeId) {
      return this.$store.state.data.category_types.find(ct => ct.id === categoryTypeId)?.name ?? "";
    },
    openEditModal(category) {
      if (!this.checkPermission('canUpdateTaxonomies')) {
        return;
      }
      this.edit_category = category;
    },
    confirmDelete(category) {
      if (!this.checkPermission('canDeleteTaxonomies')) {
        return;
      }
      this.$bvModal.msgBoxConfirm(`You are going to delete ${category.name} category`, {
        title: 'Please confirm',
        okVariant: 'success',
        centered: true
      }).then(value => {
        if (value) {
          this.loading = true;
          this.$api.post(`${API_ROUTES.ADMIN_TAXONOMY_CATEGORIES}/${category.id}/delete`).then(() => {
            let index = this.categories.findIndex(c => c.id === category.id);
            if (index >= 0) {
              let categories = [...this.categories];
              categories.splice(index, 1);
              this.$store.commit('data/setCategories', categories);
            }
            this.$bvToast.toast('Category has been successfully deleted', {
              title: 'Success!',
              variant: 'success'
            });
          }).catch(({response: {data}}) => {
            this.$bvToast.toast(data.message ?? 'Failed to process operation', {
              title: 'Error!',
              variant: 'danger'
            });
          }).finally(() => {
            this.loading = false;
          });
        }
      });
    },
    onCategoryAdded(category) {
      this.$bvToast.toast('Category has been successfully created', {
        title: 'Success!',
        variant: 'success'
      });
      let categories = [...this.categories];
      categories.push(category);
      this.$store.commit('data/setCategories', categories);
    },
    onCategoryEdited(category) {
      this.$bvToast.toast('Category has been successfully updated', {
        title: 'Success!',
        variant: 'success'
      });
      let categories = [...this.categories];
      let index = categories.findIndex(ct => ct.id === category.id);
      if (index >= 0) {
        categories[index] = category;
      } else {
        categories.push(category);
      }
      this.$store.commit('data/setCategories', categories);
    }
  }
}
</script>
